import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CONSULT_LINK } from "../lib/constants";
import { useSiteConfig } from "../lib/useSiteConfig";
import { resolveMediaUrl } from "../lib/mediaUrl";

const navItems = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/portfolio", label: "Portfolio" },
    { to: "/industries", label: "Industries" },
    { to: "/print", label: "Print" },
    { to: "/shop", label: "Shop" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

const mobileExtraItems = [
    { to: "/blog", label: "Blog" },
    { to: "/faq", label: "FAQ" },
    { to: "/quote", label: "Instant Estimate" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const config = useSiteConfig();
    const customLogo = config?.logo_url ? resolveMediaUrl(config.logo_url) : null;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "glass-dark border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="navbar-logo"
                    className="flex items-center gap-2 group"
                >
                    {customLogo ? (
                        <img
                            src={customLogo}
                            alt="Raj Graphics"
                            className="h-9 md:h-10 w-auto max-w-[180px] object-contain"
                            data-testid="navbar-custom-logo"
                        />
                    ) : (
                        <>
                            <div className="relative w-9 h-9 rounded-lg bg-black border border-gold/40 flex items-center justify-center overflow-hidden">
                                <span className="font-poppins font-extrabold text-gold text-lg leading-none">R</span>
                                <span className="absolute inset-0 bg-gold/0 group-hover:bg-gold/10 transition-colors" />
                            </div>
                            <div className="leading-tight">
                                <div className="font-poppins font-semibold tracking-tight text-white text-base md:text-lg">
                                    Raj <span className="text-gold">Graphics</span>
                                </div>
                                <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-zinc-500 hidden sm:block">
                                    Design · Brand · Signage
                                </div>
                            </div>
                        </>
                    )}
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`nav-link-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `relative px-3 py-2 font-inter text-sm transition-colors ${
                                    isActive ? "text-gold" : "text-zinc-300 hover:text-white"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {item.label}
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-underline"
                                            className="absolute left-2 right-2 -bottom-0.5 h-px bg-gold"
                                            transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                        />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <Link
                    to="/get-quote"
                    data-testid="navbar-cta-quote"
                    className="hidden lg:inline-flex items-center gap-2 bg-gold hover:bg-[#C5A030] text-black font-medium font-inter text-sm px-5 py-2.5 rounded-full transition-colors"
                >
                    Get a Quote
                </Link>

                <button
                    type="button"
                    aria-label="Toggle menu"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="navbar-mobile-toggle"
                    className="lg:hidden text-white p-2 rounded-md hover:bg-white/5"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className="lg:hidden glass-dark border-t border-white/10"
                        data-testid="navbar-mobile-menu"
                    >
                        <div className="px-6 py-4 flex flex-col gap-1">
                            {[...navItems, ...mobileExtraItems].map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.to === "/"}
                                    data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `font-inter text-base py-3 border-b border-white/5 ${
                                            isActive ? "text-gold" : "text-zinc-200"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/get-quote"
                                data-testid="navbar-mobile-cta"
                                className="mt-4 inline-flex items-center justify-center bg-gold text-black font-medium font-inter text-sm px-5 py-3 rounded-full"
                            >
                                Get a Quote
                            </Link>
                            <a
                                href={CONSULT_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="navbar-mobile-whatsapp"
                                className="mt-2 inline-flex items-center justify-center border border-white/15 text-white font-medium font-inter text-sm px-5 py-3 rounded-full"
                            >
                                Chat on WhatsApp
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
